import { ref } from 'vue';
import type { Language } from '@getaccept/lib-shared/src/languages/enums/translation';
import { useI18n } from 'vue-i18n';
import { AnalyticsUserTrait } from '@getaccept/lib-shared/src/types/analytics';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { messageValidator } from '../constants/message-validator.constants';
import type { IdentityProviderResponseDTO } from '../types/identity-provider-response-dto';
import * as SignupHelper from '../../helpers/signup.helper';
import type { IdentityProviderResultDTO } from '../types/identity-provider-result-dto';
import type { ErrorMessage } from '../../../common/types/error-message';
import { useGoogleTagManager } from '../../../common/composables/google-tag-manager.composable';
import type { IdentityProvider } from '../../enums/identity-provider';
import type { SignupResponse } from '../../types/signup-response';
import type { SignupPayload } from '../../types/signup-payload';

export const useSignupExternal = () => {
  const { t } = useI18n();
  const popupName = 'authWindow';

  const error = ref<ErrorMessage | undefined>();
  const provider = ref<IdentityProvider>();

  const closePopup = ({ name, url }: { name: string; url: string }) => {
    const popup = window.open('', name);
    if (!popup) return;
    popup.postMessage({ [messageValidator]: '' }, url);
  };

  const redirectToSuccessPage = (result: IdentityProviderResultDTO) => {
    const { user, entity, registration_url: registrationUrl } = result;
    const queryParams = {
      firstName: user.first_name,
      lastName: user.last_name,
      lang: user.language as Language,
      entityName: entity.entity_name,
      entityCountryCode: entity.entity_country_code,
    };

    const url = SignupHelper.getRedirectUrl({ registrationUrl, queryParams });
    window.location.href = url;
  };

  const handleError = (data: SignupResponse) => {
    bugsnagClient?.notify(new Error(JSON.stringify(data)));
    error.value = SignupHelper.getErrorMessage(data);
    useGoogleTagManager().initiateSignupError({ error: error.value?.title });
  };

  const validateIdentityProviderMessage = async ({
    event,
    url,
  }: {
    event: MessageEvent;
    url: string;
  }) => {
    const { data }: { data: IdentityProviderResponseDTO } = event;
    const { result, status, status_message } = data;

    // BE closes the popup
    if (status === 400 && status_message === 'private-gmail-not-accepted') {
      handleError({ status: 0, error: t('private-gmail-not-accepted') });
      return;
    }

    if (status !== 1 || !result) {
      closePopup({ name: popupName, url });
      handleError(data);
      return;
    }

    closePopup({ name: popupName, url });

    await useGoogleTagManager().initiateSignup({
      email: result.user.email,
      signupMethod: provider.value,
    });
    await SignupHelper.setAnalyticsSignupSuccess(result.user.email, result.registration_url);
    redirectToSuccessPage(result);
  };

  const initialize = (
    provider: IdentityProvider,
    payload: Omit<SignupPayload, 'recaptchaToken'> & { countryName: string }
  ) => {
    const analyticsData = {
      [AnalyticsUserTrait.SignupMethod]: provider,
      [AnalyticsUserTrait.SignupStep]: 1,
      [AnalyticsUserTrait.Country]: payload.countryName,
    };
    SignupHelper.setAnalyticsSignup(analyticsData);
    SignupHelper.launchIdentifier({
      identityProvider: provider,
      templateId: payload.templateId,
      registrationCampaignId: payload.registrationCampaign,
      language: payload.language,
    });
  };
  return { initialize, validateIdentityProviderMessage, error };
};
